// src/components/PIIAnonymizerPage/PIIFileDropzone.jsx
import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { CloudUploadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { MAX_UPLOAD_MB, MAX_UPLOAD_BYTES, splitBySize, notifyTooBig } from '../../../shared/uploadLimits';
import { reportPrecheckEvent } from '../../../shared/precheck';

const SUPPORTED_EXTENSIONS = ['.pdf', '.docx'];

const isSupportedFile = (file) => {
  const name = (file?.name || '').toLowerCase();
  return SUPPORTED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const PIIFileDropzone = ({ disabled, onFilesAdded }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const processFiles = (fileList) => {
    if (!fileList || !fileList.length) return;

    const files = Array.from(fileList);
    const supported = files.filter(isSupportedFile);
    const rejected = files.filter((f) => !isSupportedFile(f));

    if (rejected.length > 0) {
      enqueueSnackbar(
        t('PIIFileDropzone.unsupportedFiles', {
          files: rejected.map((f) => f.name).join(', ')
        }),
        { variant: 'warning' }
      );
    }

    if (supported.length === 0) {
      enqueueSnackbar(t('PIIFileDropzone.noSupportedFiles', 'Nevybrali ste žiadne podporované súbory.'), {
        variant: 'error'
      });
      return;
    }

    // Limit veľkosti sa kontroluje pred uploadom (#162) — veľký súbor sa nepridá.
    const { ok, tooBig } = splitBySize(supported);
    notifyTooBig(t, enqueueSnackbar, tooBig);
    tooBig.forEach((f) => reportPrecheckEvent({ event: 'file_too_large', op: 'anonymize', filename: f.name, size_bytes: f.size, limit_mb: MAX_UPLOAD_BYTES / 1024 / 1024 }));
    if (ok.length === 0) return;

    onFilesAdded(ok);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    processFiles(e.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e) => {
    if (disabled) return;
    processFiles(e.target.files);
    e.target.value = ''; // allow re-selecting same files
  };

  const handleClick = () => {
    if (disabled) return;
    document.getElementById('pii-file-input')?.click();
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={handleClick}
      sx={{
        border: '2px dashed',
        borderColor: 'primary.300',
        bgcolor: 'primary.50',
        borderRadius: 3,
        p: 4,
        textAlign: 'center',
        mb: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          bgcolor: disabled ? 'primary.50' : 'primary.100',
          borderColor: disabled ? 'primary.400' : 'primary.main',
        }
      }}
    >
      <Stack alignItems="center" spacing={1.5}>
        <CloudUploadOutlined sx={{ fontSize: 48, color: 'primary.main', opacity: 0.8 }} />
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.900' }}>
                {t('PIIFileDropzone.dropzoneTitle', 'Kliknite alebo presuňte súbory sem')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.800', mt: 0.5 }}>
                {t('PIIFileDropzone.dropzoneSubtitle', { defaultValue: 'Podporované formáty: .pdf, .docx · najviac {{limit}} MB na súbor', limit: MAX_UPLOAD_MB })}
            </Typography>
        </Box>
      </Stack>

      <input
        type="file"
        id="pii-file-input"
        multiple
        accept=".pdf,.docx"
        style={{ display: 'none' }}
        onChange={handleInputChange}
        disabled={disabled}
      />
    </Box>
  );
};

export default PIIFileDropzone;